/**
 * Playback clock.
 *
 * The current time is intentionally NOT React state - pushing 60fps updates
 * through React would re-render the whole editor. Instead this clock owns a
 * requestAnimationFrame loop and notifies subscribers (the caption canvas, the
 * transport playhead) directly. Components that must render the active word
 * (e.g. transcript highlight) throttle their own React updates.
 */
export type TimeListener = (timeMs: number, playing: boolean) => void;

export class PlaybackClock {
  private media: HTMLVideoElement | null = null;
  private listeners = new Set<TimeListener>();
  private raf = 0;
  private internalMs = 0;
  private lastTs = 0;
  private playing = false;
  private durationMs = 0;

  attachMedia(media: HTMLVideoElement | null): void {
    this.media = media;
  }

  setDuration(ms: number): void {
    this.durationMs = ms;
  }

  subscribe(fn: TimeListener): () => void {
    this.listeners.add(fn);
    fn(this.timeMs(), this.playing);
    return () => this.listeners.delete(fn);
  }

  timeMs(): number {
    if (this.media) return this.media.currentTime * 1000;
    return this.internalMs;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  play(): void {
    if (this.playing) return;
    this.playing = true;
    this.lastTs = performance.now();
    if (this.media) void this.media.play().catch(() => undefined);
    this.loop();
  }

  pause(): void {
    this.playing = false;
    if (this.media) this.media.pause();
    cancelAnimationFrame(this.raf);
    this.emit();
  }

  toggle(): void {
    this.playing ? this.pause() : this.play();
  }

  seek(ms: number): void {
    const clamped = Math.max(0, this.durationMs ? Math.min(ms, this.durationMs) : ms);
    if (this.media) this.media.currentTime = clamped / 1000;
    else this.internalMs = clamped;
    this.emit();
  }

  private loop = (): void => {
    if (!this.playing) return;
    const now = performance.now();
    if (this.media) {
      // Media reached its end - stop so the transport flips back to "play".
      if (this.media.ended || this.media.paused) {
        this.pause();
        return;
      }
    } else {
      this.internalMs += now - this.lastTs;
      if (this.durationMs && this.internalMs >= this.durationMs) {
        this.internalMs = this.durationMs;
        this.pause();
        return;
      }
    }
    this.lastTs = now;
    this.emit();
    this.raf = requestAnimationFrame(this.loop);
  };

  private emit(): void {
    const t = this.timeMs();
    for (const fn of this.listeners) fn(t, this.playing);
  }
}

export const playbackClock = new PlaybackClock();
